'use client';

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import SvgCircleBlur from '@/assets/icons/iconCircleBlur.svg';
import SvgClose from '@/assets/icons/iconCloseInCircle.svg';
import SvgSearch from '@/assets/icons/iconSearch.svg';
import { classNames } from '@/shared/lib/classNames/classNames';

interface ImageZoomProps {
  fullSrc: string;
  alt?: string;
  children: React.ReactNode;
  iconPosition?: 'middle' | 'bottom-left';
}

export const ImageZoom: React.FC<ImageZoomProps> = ({
  fullSrc,
  alt = '',
  children,
  iconPosition = 'middle',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [mounted, setMounted] = useState(false);

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dragged = useRef(false);

  const [pinchStartDist, setPinchStartDist] = useState<number | null>(null);
  const [pinchStartScale, setPinchStartScale] = useState<number>(1);

  const imgRef = useRef<HTMLImageElement>(null);
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    setMounted(true);

    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', listener);

    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, []);

  useEffect(() => {
    if (!mounted || !isOpen) return;

    const clampOffset = (x: number, y: number, customScale: number = scale) => {
      const cw = window.innerWidth;
      const ch = window.innerHeight;
      const imgW = displaySize.width * customScale;
      const imgH = displaySize.height * customScale;
      const maxX = Math.max(0, (imgW - cw) / 2);
      const maxY = Math.max(0, (imgH - ch) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, x)),
        y: Math.min(maxY, Math.max(-maxY, y)),
      };
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      dragged.current = true;
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setDragStart({ x: e.clientX, y: e.clientY });
      setOffset((prev) => clampOffset(prev.x + dx, prev.y + dy));
    };
    const onMouseUp = () => setIsDragging(false);

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchStartDist !== null) {
        e.preventDefault();
        const [t1, t2] = e.touches;
        const dist = Math.hypot(
          t2.clientX - t1.clientX,
          t2.clientY - t1.clientY,
        );
        const newScale = Math.max(
          1,
          Math.min((pinchStartScale * dist) / pinchStartDist, 2.5),
        );
        setScale(newScale);
        setOffset((prev) => clampOffset(prev.x, prev.y, newScale));
        return;
      }

      if (!isDragging) return;
      e.preventDefault();
      dragged.current = true;
      const touch = e.touches[0];
      const dx = touch.clientX - dragStart.x;
      const dy = touch.clientY - dragStart.y;
      setDragStart({ x: touch.clientX, y: touch.clientY });
      setOffset((prev) => clampOffset(prev.x + dx, prev.y + dy));
    };
    const onTouchEnd = (e: TouchEvent) => {
      setIsDragging(false);
      if (e.touches.length < 2) {
        setPinchStartDist(null);
        setPinchStartScale(scale);
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [
    mounted,
    isOpen,
    isDragging,
    dragStart,
    pinchStartDist,
    pinchStartScale,
    scale,
    displaySize,
  ]);

  const handleImageLoad = () => {
    if (imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      setDisplaySize({ width: rect.width, height: rect.height });
    }
  };

  const handleThumbnailClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  const handleOverlayClick = () => {
    setIsOpen(false);
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dragged.current) {
      dragged.current = false;
      return;
    }
    setScale((prev) => (prev > 1 ? 1 : 2));
    setOffset({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (scale <= 1) return;
    e.preventDefault();
    dragged.current = false;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLImageElement>) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

      setPinchStartDist(dist);
      setPinchStartScale(scale);
    } else if (e.touches.length === 1 && scale > 1) {
      e.preventDefault();
      dragged.current = false;

      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  };

  const handleTouchEndImg = (e: React.TouchEvent) => {
    setIsDragging(false);
    if (pinchStartDist !== null && e.touches.length < 2) {
      setPinchStartDist(null);
      setPinchStartScale(scale);
      if (scale <= 1) setOffset({ x: 0, y: 0 });
    }
  };

  const imgCls = classNames({
    cls: 'touch-none select-none',
    mods: {
      'cursor-grab': scale > 1 && !isDragging,
      'cursor-grabbing': scale > 1 && isDragging,
      'cursor-zoom-in': scale === 1,
    },
  });

  return (
    <>
      <div onClick={handleThumbnailClick} className="relative cursor-zoom-in">
        <div
          className={classNames({
            cls: 'absolute z-10 flex h-10 w-10 transform items-center justify-center overflow-hidden rounded-full',
            mods: {
              'top-1/2 left-1/2 -translate-1/2': iconPosition === 'middle',
              'bottom-[16px] left-[16px]': iconPosition === 'bottom-left',
            },
          })}
        >
          <SvgCircleBlur className="absolute inset-0" />
          <SvgSearch className="relative w-6" />
        </div>

        {children}
      </div>
      {mounted &&
        isOpen &&
        createPortal(
          <div
            onClick={handleOverlayClick}
            className="fixed inset-0 z-101 flex touch-none items-center justify-center bg-black/70 lg:px-20"
          >
            <img
              ref={imgRef}
              src={fullSrc}
              alt={alt}
              onLoad={handleImageLoad}
              onClick={handleImageClick}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEndImg}
              className={imgCls}
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                transition:
                  isDragging || pinchStartDist ? 'none' : 'transform 0.3s ease',
              }}
            />

            <SvgClose
              className="absolute top-[40px] right-[40px] cursor-pointer max-sm:top-[20px] max-sm:right-[20px]"
              onClick={handleOverlayClick}
            />
          </div>,
          document.body,
        )}
    </>
  );
};
